import * as React from "react";
import { ControllerStateAndHelpers } from "downshift";
import { darken } from "polished";
import styled from "styled-components";
import { debounce } from "lodash";

import { ISelectItem } from "../Forms";
import { Icon } from "../Icon";
import { LoadingSpinner } from "../LoadingSpinner";
import { Icons } from "../Icon/types";
import MultiDownshift from "./MultiDownshift";

export interface IMultiSelect {
  disabled?: boolean;
  disabledOptions?: string[];
  fieldLogic?: (affected: string, oldSelected: string[]) => void;
  options: ISelectItem[];
  placeholder?: string;
  loading?: boolean;
  defaultValues?: ISelectItem[];
  handleChange?: (
    action: "add" | "remove",
    affectedItem: ISelectItem | null,
    oldSelectedItems: ISelectItem[],
    stateAndHelpers?: ControllerStateAndHelpers<ISelectItem>
  ) => void;
  handleSpecificSearch?: (search: string | null) => void;
  debounceTime?: number;
  singleSelection?: boolean;
}

const StyledInput = styled.input<{ isOpen: boolean }>`
  outline: none;
  height: 40px;
  width: 100%;
  font-size: ${({ theme }) => theme.fonts.sizes.p};
  line-height: 1em;
  padding-left: 10px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: ${({ theme, isOpen }) => (!isOpen ? theme.borderRadius : `${theme.borderRadius} ${theme.borderRadius} 0px 0px`)};
  box-sizing: border-box;
  max-width: 400px;
`;

const ControllerButton = styled.button`
  background-color: transparent;
  border: none;
  position: absolute;
  right: 0;
  top: 0;
  cursor: pointer;
  width: 47;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
  &:focus {
    outline: none;
  }
`;

const SelectedItemExternalBox = styled.div`
  margin: 2px;
  padding-top: 2px;
  padding-bottom: 2px;
  padding-left: 8px;
  padding-right: 8px;
  display: inline-block;
  background-color: #ccc;
  border-radius: ${({ theme }) => theme.borderRadius};
`;

const SelectedItemInternalBox = styled.div`
  display: grid;
  grid-gap: 6px;
  grid-auto-flow: column;
  align-items: center;
`;

const SelectedItemText = styled.span`
  font-size: ${({ theme }) => theme.fonts.sizes.p};
`;

const Item = styled.li<{ isActive: boolean; isSelected: boolean; disabled?: boolean; }>`
  position: relative;
  cursor: pointer;
  display: block;
  height: auto;
  text-align: left;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  box-shadow: none;
  height: 40px;
  padding-left: 10px;
  line-height: 40px;
  white-space: normal;
  font-size: ${({ theme }) => theme.fonts.sizes.p};
  ${({ theme, isActive }) => `
    color:  ${isActive ? darken(0.1, theme.colors.lightestGrey) : "inherit"};
    background-color: ${isActive ? theme.colors.primaryDark : "inherit"};
  `}

  ${({ disabled }) => disabled ? `
    cursor: not-allowed;
    opacity: 0.3;
  ` : ""}
`;

const NoResultsBox = styled.span<{ textAlign?: string }>`
  font-size: ${({ theme }) => theme.fonts.sizes.p};
  line-height: 40px;
  padding-left: 10px;
  display: block;
  ${({ textAlign }) => `
    text-align:  ${textAlign ? textAlign : "inherit"};
  `}
`;

const RemoveItemButton = styled.button`
  cursor: pointer;
  line-height: 0.8;
  border: none;
  background-color: transparent;
  padding: 0;
  font-size: ${({ theme }) => theme.fonts.sizes.p};
  &:focus {
    outline: none;
  }
`;

const ArrowIcon = ({ isOpen }: { isOpen: boolean }) => {
  return (
    <Icon
      $fillColor="darkGrey"
      icon={isOpen ? Icons.CHEVRONUP : Icons.CHEVRONDOWN}
      size={24}
    />
  );
};

const Menu = styled.ul<{ loading: "true" | "false"; isOpen: boolean }>`
  outline: none;
  box-sizing: border-box;
  padding: 0;
  margin-top: 0;
  position: absolute;
  width: 100%;
  z-index: 1;
  background: white;
  overflow-y: auto;
  overflow-x: hidden;
  transition: opacity 0.1s ease;
  border: ${({ theme, isOpen }) => (isOpen ? `1px solid ${theme.colors.lightGrey}}` : "none")};
  border-radius: 0px 0px ${({ theme }) => theme.borderRadius} ${({ theme }) => theme.borderRadius};
  box-shadow: ${({ theme, isOpen }) => (isOpen ? theme.shadows.default : "none")};
  max-width: 400px;
  max-height: 400px;

  ${({ loading }) => `
    text-align: ${loading === "true" ? "center" : "inherit"};
  `}
`;

/**
 * Multiselect component.
 */
const MultiSelect: React.ReactFCWithChildren<IMultiSelect> = ({
  loading = false,
  handleChange,
  disabled,
  options,
  placeholder = "Start typing...",
  defaultValues,
  disabledOptions,
  handleSpecificSearch,
  debounceTime,
  singleSelection
}) => {
  const itemToString = () => {
    return "";
  };

  const [ triedSpecificSearch, setTriedSpecificSearch ] = React.useState<string | null>("");
  const [ isDisabled, setIsDisabled ] = React.useState(disabled);

  const disableCallBack = React.useCallback((disable: boolean) => {
    setIsDisabled(disable);
  }, []);

  //stop input from searching immediately
  const debouncedSpecificSearch = React.useMemo(() => {
    if (handleSpecificSearch) {
      return debounce((search: string | null) => {
        if (search) {
          handleSpecificSearch(search);
        }
      }, debounceTime ?? 500);
    } else {
      return null;
    }
  }, [ debounceTime, handleSpecificSearch ]);

  const getItems = (inputValue: string | null, selectedItems: ISelectItem[]): ISelectItem[] => {
    const selectedNames = selectedItems ? selectedItems.map(selectedItem => selectedItem.text) : [];
    let items: ISelectItem[] = [ ...options ];

    // Filter by input string.
    if (inputValue) {
      items = items.filter(item => item.text.toLowerCase().includes(inputValue.toLowerCase()));
    }

    // Remove already selected.
    items = items.filter(element => !selectedNames.some(filterElement => filterElement === element.text));

    if (inputValue !== triedSpecificSearch && debouncedSpecificSearch) {
      debouncedSpecificSearch(inputValue);
      setTriedSpecificSearch(inputValue);
    }

    //if there's an input, sort the list alphabetically
    if (inputValue) {
      items.sort((a, b) => a.text.localeCompare(b.text));
      //if there isnt an input go back to initial list if Specific search is available
    }

    return items;
  };

  return (
    <MultiDownshift
      disabledOptions={disabledOptions}
      handleChange={handleChange}
      itemToString={itemToString}
      defaultValues={defaultValues}
      singleSelection={singleSelection}
      disableCallBack={disableCallBack}
    >
      {({
        getInputProps,
        getToggleButtonProps,
        // @ts-ignore
        getRemoveButtonProps,
        // removeItem,
        isOpen,
        inputValue,
        // @ts-ignore
        selectedItems,
        getItemProps,
        toggleMenu,
        highlightedIndex
      }) => (
        <div
          style={{
            width: "100%",
            maxWidth: "400px"
          }}
        >
          <div
            onClick={() => {
              toggleMenu();
              isOpen = !isOpen;
            }}
          >
            <div>
              <div
                style={{ position: "relative" }}
              >
                <StyledInput
                  {...getInputProps()}
                  placeholder={isDisabled ? "" : placeholder}
                  disabled={isDisabled}
                  isOpen={isDisabled ? false : isOpen}
                />

                <ControllerButton
                  {...getToggleButtonProps({
                    // prevents the menu from immediately toggling
                    // closed (due to our custom click handler above).
                    onClick(event: any) {
                      event.stopPropagation();
                    }
                  })}
                >
                  <ArrowIcon isOpen={isDisabled ? false : isOpen} />
                </ControllerButton>
              </div>
            </div>

            <Menu
              isOpen={isDisabled ? false : isOpen}
              loading={loading ? "true" : "false"}
            >
              {/* // Show loading spinner. */}
              {(isOpen && !isDisabled ? (
                loading ? (
                  <div>
                    <NoResultsBox textAlign="center">
                      <LoadingSpinner
                        type="circular"
                        position="inline"
                        size={15}
                        color="darkGrey"
                      />
                    </NoResultsBox>
                  </div>
                ) : getItems(inputValue, selectedItems).length === 0 ? (
                  <NoResultsBox>
                    No results
                  </NoResultsBox>
                ) : (
                  // Show items.
                  getItems(inputValue, selectedItems).map((item: ISelectItem, index: number) => {
                    return (
                      <Item
                        key={index}
                        isActive={highlightedIndex === index}
                        disabled={disabledOptions?.includes(item.value) || isDisabled}
                        isSelected={selectedItems.some((selectedItem: ISelectItem) => item.value === selectedItem.value)}
                        {...getItemProps({
                          item,
                          index
                        })}
                      >
                        {item.text}
                      </Item>
                    );
                  })
                )
              ) : null)}
            </Menu>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                marginTop: "10px"
              }}
            >
              {selectedItems.length > 0 &&
                selectedItems.map((item: ISelectItem, index: number) => (
                  <SelectedItemExternalBox key={index}>
                    <SelectedItemInternalBox>
                      <SelectedItemText>
                        {item.text}
                      </SelectedItemText>

                      <RemoveItemButton
                        type="button"
                        {...getRemoveButtonProps({ item })}
                      >
                        <Icon
                          $fillColor="darkGrey"
                          icon={Icons.CLOSE}
                          size={24}
                        />
                      </RemoveItemButton>
                    </SelectedItemInternalBox>
                  </SelectedItemExternalBox>
                ))}
            </div>
          </div>
        </div>
      )}
    </MultiDownshift>
  );
};

export default MultiSelect;
